import type * as React from 'react';
import { useState } from 'react';
import { Button as MuiButton, Grid, IconButton } from '@mui/material';
import { tss } from 'tss-react/mui';
import { FormattedDate, FormattedMessage } from 'react-intl';
import formatDateForAPI from '@/services/formatDateForAPI';
import { ProjectSurveyStatus } from '@/schema.graphql';
import { Button } from '../Button';
import { AddIcon, ArrowLeftIcon } from '../icons';

const useSurveyScheduleCalendarStyles = tss
    .withParams<{ hasSelectedDates: boolean }>()
    .create(({ theme, hasSelectedDates }) => ({
        schedule_calendar: {},
        calendar_month: {
            [theme.breakpoints.up('sm')]: {
                padding: 8,
                textAlign: 'center',
            },
        },
        button_month: {
            fontSize: 14,
            height: 40,
            width: 40,
            marginBottom: 12,
            padding: 0,
            [theme.breakpoints.up('sm')]: {
                fontSize: 14,
                width: 38,
                height: 38,
                marginTop: '-7px !important',
                marginLeft: '-7px !important',
            },
        },
        button_month_selected: {
            backgroundColor: theme.palette.primary.main,
            color: theme.bino.color.white,
            '&[disabled]': {
                backgroundColor: theme.bino.color.darkGray + '!important',
                color: theme.bino.color.white + '!important',
            },
            '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.bino.color.white,
            },
        },
        button_existing_month_selected: {
            backgroundColor: theme.bino.color.darkGray + '!important',
            color: theme.bino.color.white + '!important',
            '&:hover': {
                backgroundColor: theme.bino.color.gray,
                color: theme.bino.color.white,
            },
        },
        button_completed: {
            backgroundColor: theme.bino.color.darkGreen + '!important',
            color: theme.bino.color.white + '!important',
            '&:hover': {
                backgroundColor: theme.bino.color.green,
                color: theme.bino.color.white,
            },
        },
        addYearsIcon: {
            marginRight: theme.spacing(1),
        },
        buttons: {
            marginTop: 40,
        },
        showMoreYearsContainer: {
            display: 'flex',
            justifyContent: 'center',
        },
        form: {
            [theme.breakpoints.up('sm')]: {
                marginTop: hasSelectedDates ? 20 : 0,
                '& input': {
                    width: '100%',
                },
                '& select': {
                    textAlign: 'left',
                },
            },
        },
        back: {
            [theme.breakpoints.up('sm')]: {
                width: `100%`,
                marginLeft: 15,
            },
        },
        next: {
            [theme.breakpoints.up('sm')]: {
                width: `100%`,
                marginLeft: 15,
            },
        },
        link: {
            [theme.breakpoints.up('sm')]: {
                textDecoration: 'none',
            },
        },

        // ----------- new styles -----------------
        theYearContainer: {
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
            '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                background:
                    theme.palette.mode === 'dark'
                        ? theme.bino.color.darkGray
                        : theme.bino.color.lightGray,
                height: 1,
                width: '100%',
                marginTop: 0,
                top: '50%',
            },
            '&>div': {
                background: theme.palette.background.paper,
                border: `solid 1px ${
                    theme.palette.mode === 'dark'
                        ? theme.bino.color.darkGray
                        : 'transparent'
                }`,
                borderRadius: 4,
                zIndex: 0,
                paddingLeft: 8,
                paddingRight: 8,
            },
        },
    }));

type SurveyScheduleProps = {
    selectedDates: ReadonlyArray<string>;
    onToggle(date: string): void;
    next?: React.FormEventHandler;
    save?: React.FormEventHandler;
    back?: React.MouseEventHandler;
    saving?: boolean;
    surveys: Array<{ status: string; surveyDate: string }>;
};
const addMoreYears = (y: number) => y + 2;

const isPreliminary = (status: string) => {
    return (
        status === ProjectSurveyStatus.Preliminary ||
        status === ProjectSurveyStatus.BehindPreliminarySchedule
    );
};

const isCompleted = (status: string) =>
    status === ProjectSurveyStatus.Completed;

function SurveyScheduleCalendar({
    next,
    back,
    save,
    saving,
    onToggle,
    surveys,
    selectedDates,
}: SurveyScheduleProps) {
    const { classes, cx } = useSurveyScheduleCalendarStyles({
        hasSelectedDates: selectedDates.length > 0,
    });
    const arrayOfSelectedDates = selectedDates.map((date) => new Date(date));
    const arrayOfSurveyDates = surveys.map((s) => new Date(s.surveyDate));
    const today = new Date();

    const startYear = Math.min(
        ...[today, ...arrayOfSurveyDates, ...arrayOfSelectedDates].map((d) =>
            d.getUTCFullYear(),
        ),
    );
    const [endYear, setEndYear] = useState(() =>
        Math.max(
            startYear + 2,
            ...[...arrayOfSurveyDates, ...arrayOfSelectedDates].map((d) =>
                d.getUTCFullYear(),
            ),
        ),
    );

    type CalendarYear = {
        year: number;
        months: Array<CalendarMonth>;
    };
    type CalendarMonth = {
        date: Date;
        selected: boolean;
        hasSurvey: boolean;
        status: string | null;
        disabled: boolean;
    };
    const calendar: Array<CalendarYear> = [];
    for (let year = startYear; year <= endYear; year++) {
        const months = [];
        for (let month = 0; month < 12; month++) {
            const date = new Date(Date.UTC(year, month, 1));
            const sameMonth = (d: Date) =>
                d.getUTCFullYear() === year && d.getUTCMonth() === month;
            //console.log(year, month, 'Same months as today', sameMonth(today));

            months.push({
                date,
                selected: arrayOfSelectedDates.some(sameMonth),
                hasSurvey: arrayOfSurveyDates.some(sameMonth),
                status: surveys.map((s) =>
                    sameMonth(new Date(s.surveyDate)) ? s.status : null,
                )[0],
                disabled:
                    (date < today && !sameMonth(today)) ||
                    arrayOfSurveyDates.some((surveyDate) => date < surveyDate),
            });
        }
        calendar.push({
            year: year,
            months: months,
        });
    }

    console.log({ calendar });
    const getYearContent = (calendar: CalendarYear) => (
        <Grid container spacing={0}>
            {calendar.months.map((month, index) => (
                <Grid item xs={2} sm={2} key={`month${calendar.year}-${index}`}>
                    <div className={classes.calendar_month}>
                        <IconButton
                            onClick={() =>
                                onToggle(formatDateForAPI(month.date))
                            }
                            className={cx({
                                [classes.button_month]: true,
                                [classes.button_month_selected]: month.status
                                    ? isPreliminary(month.status) &&
                                      month.selected
                                    : month.selected,
                                [classes.button_existing_month_selected]:
                                    month.status
                                        ? !isPreliminary(month.status) &&
                                          !isCompleted(month.status) &&
                                          month.hasSurvey
                                        : month.hasSurvey,
                                [classes.button_completed]: month.status
                                    ? isCompleted(month.status)
                                    : false,
                            })}
                            disabled={month.disabled}
                            key={`${calendar.year}-${index}`}
                            size="large"
                        >
                            <FormattedDate
                                value={month.date}
                                month="short"
                                timeZone="UTC"
                            />
                        </IconButton>
                    </div>
                </Grid>
            ))}
        </Grid>
    );

    const getCalendarContent = (calendar: CalendarYear) => (
        <div key={calendar.year}>
            <div className={classes.theYearContainer}>
                <div>{calendar.year}</div>
            </div>
            {getYearContent(calendar)}
        </div>
    );

    return (
        <form className={classes.form} onSubmit={next || save}>
            <div className={classes.schedule_calendar}>
                {calendar.map((calendar) => getCalendarContent(calendar))}
            </div>

            <div className={classes.showMoreYearsContainer}>
                <MuiButton
                    variant="text"
                    onClick={() => setEndYear(addMoreYears)}
                    className={classes.link}
                >
                    <AddIcon className={classes.addYearsIcon} />
                    <FormattedMessage defaultMessage="Show more years" />
                </MuiButton>
            </div>

            <Grid container spacing={4} className={classes.buttons}>
                {back && (
                    <Grid item xs={12} sm={6}>
                        <Button
                            color="secondary"
                            type="button"
                            className={classes.back}
                            onClick={back}
                        >
                            <ArrowLeftIcon height={12} width={12} />
                            <FormattedMessage defaultMessage="Back" />
                        </Button>
                    </Grid>
                )}
                {next && (
                    <Grid item xs={12} sm={6}>
                        <Button
                            className={classes.next}
                            disabled={selectedDates.length <= 0 || saving}
                            type="submit"
                        >
                            <FormattedMessage defaultMessage="Next" />
                        </Button>
                    </Grid>
                )}
                {save && (
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            disabled={selectedDates.length <= 0 || saving}
                            type="submit"
                        >
                            <FormattedMessage defaultMessage="Save" />
                        </Button>
                    </Grid>
                )}
            </Grid>
        </form>
    );
}

export default SurveyScheduleCalendar;
